import { MapCageZone } from 'const/Map/MapCageZone';
import { MapStyle } from 'const/Map/MapStyle';
import { useCageZones } from 'hooks/useCageZones';
import { GeoJSONSource, Map } from 'mapbox-gl';
import React, { useState } from 'react';
import { Region } from 'types/orderTypes';
import './Legend.scss';

interface MapboxLegendProps {
  map: Map;
  mapStyle: MapStyle;
  region: Region;
  windows: [string, string][];
  changeMapStyle: (style: MapStyle) => void;
}

export const MapboxLegend = ({ map, ...props }: MapboxLegendProps) => {
  const [expand, setExpand] = useState(true);
  const [enableCageZones, setEnableCageZones] = useState(false);

  const cageZones = useCageZones(props.region);

  const onToggleExpand = () => {
    setExpand(!expand);
  };

  const changeModeLabel = () => {
    return props.mapStyle === MapStyle.Normal ? 'Satellite map' : 'Normal map';
  };

  const onChangeMode = () => {
    let currentMode = props.mapStyle;
    if (currentMode === MapStyle.Normal) {
      currentMode = MapStyle.Satelite;
    } else {
      currentMode = MapStyle.Normal;
    }
    props.changeMapStyle(currentMode);
    setEnableCageZones(false);
  };

  const showCageZones = () => {
    /* Reset source */
    if (map.getSource(MapCageZone.SourceId)) {
      map.removeSource(MapCageZone.SourceId);
    }
    map.addSource(MapCageZone.SourceId, {
      type: 'geojson',
      data: cageZones,
    });

    map.addLayer({
      id: MapCageZone.Layer,
      type: 'fill',
      source: MapCageZone.SourceId,
      layout: {},
      paint: {
        'fill-color': 'rgba(0,0,0,0.2)',
      },
    });
    map.addLayer({
      id: MapCageZone.Border,
      type: 'line',
      source: MapCageZone.SourceId,
      layout: {},
      paint: {
        'line-color': 'black',
        'line-width': 1,
      },
    });
    map.addLayer({
      id: MapCageZone.Text,
      type: 'symbol',
      source: MapCageZone.SourceId,
      layout: {
        'text-field': '{zone}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 24,
      },
    });
  };

  const hideCageZones = () => {
    if (map.getLayer(MapCageZone.Layer)) {
      map.removeLayer(MapCageZone.Layer);
    }

    if (map.getLayer(MapCageZone.Text)) {
      map.removeLayer(MapCageZone.Text);
    }

    if (map.getLayer(MapCageZone.Border)) {
      map.removeLayer(MapCageZone.Border);
    }
  };

  const onToggleCageZones = () => {
    if (enableCageZones) {
      hideCageZones();
    } else {
      showCageZones();
    }
    setEnableCageZones(!enableCageZones);
  };

  return (
    <div className="legend">
      <div style={{ maxHeight: expand ? 500 : 0, overflow: 'hidden' }}>
        <div>Click to select</div>
        <div>Right click to show info</div>
        <button onClick={onChangeMode} className="button">
          {changeModeLabel()}
        </button>
        <button onClick={onToggleCageZones} className="button">
          {enableCageZones ? 'Hide cage zones' : 'Show cage zones'}
        </button>
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <h6>Delivery windows</h6>
          {props.windows.map(([time, color]) => (
            <div key={time} className="windows">
              <div style={{ width: 16, height: 16, backgroundColor: color }} />
              {time}
            </div>
          ))}
          <div className="windows">
            <div
              style={{ width: 16, height: 16, backgroundColor: '#661aff' }}
            />
            Multiple windows
          </div>
        </div>
      </div>
      <button onClick={onToggleExpand} className="button">
        {expand ? 'Collapse' : 'Expand'}
      </button>
    </div>
  );
};
